import type { ChatSettingsFormData } from '@choco-admin/types';
import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FiInfo } from 'react-icons/fi';

import type { SelectOption } from '../../../../../../../../components/FormComponents';
import { useListSubCatalogs } from '../../../../../../hooks';
import {
  DEFAULT_SUB_CATALOG_VALUE,
  mergeSubCatalogOptions,
  normalizeSubCatalogSelection,
} from '../../../../../../utils/subCatalogSelection';

import { SearchableCatalogPicker } from './SearchableCatalogPicker';

interface SubCatalogSelectProps {
  /** Distributor entity id whose custom catalogs are selectable. */
  supplierId?: string;
}

export const SubCatalogSelect: React.FC<SubCatalogSelectProps> = ({
  supplierId,
}) => {
  const { control } = useFormContext<ChatSettingsFormData>();
  const { subCatalogs, isLoading, error } = useListSubCatalogs(supplierId);

  const defaultOption = useMemo<SelectOption>(
    () => ({
      value: DEFAULT_SUB_CATALOG_VALUE,
      label: 'Default catalog',
      description: 'No custom catalog — the buyer sees all products',
    }),
    []
  );

  if (!supplierId) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Custom catalog
        </label>
        <p className="text-sm text-default-500">
          Assign a distributor to this chat to manage its custom catalog.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Controller
        name="subCatalog"
        control={control}
        render={({ field }) => {
          const customCatalogOptions = mergeSubCatalogOptions(
            subCatalogs,
            field.value ?? []
          );
          const options = [defaultOption, ...customCatalogOptions];
          const selectedIds = (field.value ?? []).map((option) => option.value);
          const selectedValues =
            selectedIds.length > 0 ? selectedIds : [DEFAULT_SUB_CATALOG_VALUE];

          return (
            <SearchableCatalogPicker
              options={options}
              value={selectedValues}
              isLoading={isLoading}
              isDisabled={isLoading || Boolean(error)}
              errorMessage={
                error ? 'Failed to load custom catalogs.' : undefined
              }
              onSelectionChange={(values) => {
                const nextIds = normalizeSubCatalogSelection(
                  selectedIds,
                  values
                );
                field.onChange(
                  nextIds.map((id) => {
                    const option = customCatalogOptions.find(
                      (subCatalog) => subCatalog.value === id
                    );
                    const currentOption = (field.value ?? []).find(
                      (selectedOption) => selectedOption.value === id
                    );

                    return {
                      value: id,
                      label: option?.label ?? currentOption?.label ?? id,
                      supplierId,
                    };
                  })
                );
              }}
            />
          );
        }}
      />

      <div className="flex items-start gap-2 text-xs text-default-500">
        <FiInfo className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Catalog assignment is managed here. For LOX distributors, the catalog{' '}
          <strong>content</strong> is still managed in the Retool app.
        </span>
      </div>
    </div>
  );
};
